import SharedDialog from '../../../shared/shared-dialog';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { copyDocument, UserKeys } from '../../../api/users-api';
import { handleErrors } from '../../../utils/errors';
import { Doc } from '../../../../types';
import { Dispatch, SetStateAction, useState } from 'react';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import { useStore } from '../../state/zustand-store';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../shared/analytics/module-list';
import {
  DriveRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import { Messages } from '../../../utils/message-constants';

import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

interface DriveSearchCopyDialogProps {
  open: boolean;
  handleClose: () => void;
  selectedDoc: Doc;
  searchedDocs: Doc[];
  setSearchedDocs: Dispatch<SetStateAction<Doc[]>>;
  changeLogRoles: string[];
}

export default function DriveSearchCopyDialog({
  open,
  handleClose,
  selectedDoc,
  searchedDocs,
  setSearchedDocs,
  changeLogRoles,
}: DriveSearchCopyDialogProps) {
  const queryClient = useQueryClient();
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const { setSnackbar } = useStore();
  const [newName, setNewName] = useState('Copy of ' + selectedDoc.title);

  const { mutate, isPending: isCopying } = useMutation({
    mutationFn: () =>
      copyDocument(
        selectedDoc.foundUnder,
        selectedDoc.id,
        newName.trim(),
        selectedDoc.parentId,
        changeLogMessage
      ),
    onSuccess: (doc) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Copied('Document'),
      });

      const updatedDoc = {
        ...doc,
        foundUnder: selectedDoc.foundUnder,
        parentId: selectedDoc.parentId,
        mimeType: selectedDoc.mimeType,
        owner: selectedDoc.ownerEmail
          ? selectedDoc.ownerEmail
          : selectedDoc.owners.length > 0
          ? selectedDoc.owners[0].emailAddress
          : '',
        ownerEmail: selectedDoc.ownerEmail,
        modifiedDate: new Date().getTime(),
        iconUrl: selectedDoc.iconUrl,
      };
      const newDocs = [...searchedDocs, updatedDoc];
      setSearchedDocs(newDocs);

      queryClient.invalidateQueries({
        queryKey: [UserKeys.DriveDocuments, selectedDoc.owner],
      });

      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Copying(
          'the selected document'
        ),
      });
    },
    onSettled: () => {
      createEvent(analytics.drive.drive_search.copy_drive_document, {
        module: ModuleList.modules.drive.name,
        section: ModuleList.modules.drive.sections.driveSearch,
      });
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (isAbleToSeeUIElement(changeLogRoles, DriveRoles.CanCopyDocs.value)) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title="Copy Document"
      dialogContent={
        <Stack gap={1}>
          <Typography>Provide a name for the copied document.</Typography>
          <TextField
            fullWidth
            autoFocus
            value={newName}
            label={'Name'}
            onChange={(e) => setNewName(e.target.value)}
          />
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isCopying}
          />
        </Stack>
      }
      primaryButton={{
        title: 'Copy Document',
        action: handleSubmit,
        loading: isCopying,
        disabled: isCopying || newName.trim().length === 0,
      }}
    />
  );
}
